import { useIntl } from 'react-intl'
import { useCallback, useContext, useMemo } from 'react'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import Pane from 'components/Common/Pane'
import { take } from 'lib/array/arrayUtils'
import Image from 'components/Common/Image'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR } from 'constants/offer'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import OfferBadge from 'components/Common/OfferBadge'
import OfferTilePricing from './Support/OfferTilePricing'
import FrontPageTileFlightPricing from './Support/FrontPageTileFlightPricing'
import OfferTileLoggedInButtons from './Support/OfferTileLoggedInButtons'
import TextButton from 'LuxKit/Button/TextButton'
import FrontPageTileFlightOnlyPricing from './Support/FrontPageTileFlightOnlyPricing'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import OffsetBookmarkContainer from 'tripPlanner/components/Bookmark/Common/OffsetBookmarkContainer'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import GeoContext from 'contexts/geoContext'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import OfferPropertyFlexibleCancellationPolicies from 'components/Common/CancellationPolicies/Flash/OfferPropertyFlexibleCancellationPolicies'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import WalledContent from 'components/Common/WalledContent'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { useAppSelector } from 'hooks/reduxHooks'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { isHotelPackage, isLEHotel } from 'lib/offer/offerTypes'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import useSearchBurnPoints from 'hooks/useSearchBurnPoints'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'

import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'

const Main = styled(Pane)`
  display: grid;
  position: relative;
  z-index: 0;
  grid-template-rows: ${rem(230)} auto;

  ${mediaQueryUp.tablet} {
    grid-template-rows: ${rem(360)} auto;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 67% 1fr;
    grid-template-rows: 1fr;
  }
`

const DetailContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.desktop} {
    flex: 1;
    padding: ${rem(16)};
  }
`

const DetailSegment = styled.div`
  position: relative;
  padding: ${rem(20)};
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
  }

  ${mediaQueryUp.desktop} {
    flex: 1;
    text-align: center;
    padding: ${rem(16)};
  }
`

const Logo = styled(Image)`
  width: ${rem(90)};
  height: ${rem(60)};

  ${mediaQueryUp.desktop} {
    width: ${rem(150)};
    height: ${rem(100)};
  }
`

const OfferContent = styled.div`
  > * + * {
    margin-top: ${rem(8)};

    ${mediaQueryUp.desktop} {
      margin-top: ${rem(12)};
    }
  }
`

const BottomSegmentDetails = styled.div``
const BottomSegmentCTA = styled(Group)``
const BottomSegmentExtra = styled(Group)``
const BottomSegment = styled.div`
  margin-top: ${rem(12)};
`

const BottomSegmentGrid = styled.div`
  display: grid;
  grid-column-gap: ${rem(8)};
  grid-template:
    "details cta"
    "extra extra" auto / 1fr ${rem(120)};

  > ${BottomSegmentDetails} {
    grid-area: details;
  }
  > ${BottomSegmentCTA} {
    grid-area: cta;
    align-self: end;
  }
  > ${BottomSegmentExtra} {
    grid-area: extra;
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    grid-template:
      "details"
      "." ${rem(16)}
      "cta"
      "extra" auto / 1fr;
  }
`

const UrgencyLabelsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  grid-column: 1 / 1;

  ${mediaQueryUp.desktop} {
    justify-content: center;
  }

  > * + * {
    margin-left: ${rem(4)};

    ${mediaQueryUp.desktop} {
      margin-left: ${rem(12)};
    }
  }
`

const MainImageSegment = styled.div`
  display: grid;
  gap: ${rem(4)};
  position: relative;

  ${mediaQueryUp.desktop} {
    &.additional {
      grid-template-rows: 1fr ${rem(120)};
    }
  }
`

const AdditionalImages = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    gap: ${rem(4)};
  }
`

const USDesktopRating = styled(OfferRating)`
  justify-content: center;
`

const LoyaltyPointsWrapper = styled.div`
  &:empty {
    display: none;
  }
`

const MobileLoyaltyPointsBreakpoint = styled.div`
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const DesktopLoyaltyPointsBreakpoint = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: block;
  }
`

const OfferBadgeTagWrapper = UrgencyLabelsWrapper

interface Props {
  offer: App.Offer | App.OfferSummary
  eagerLoadFirstImage?: boolean
  offerUrl: string
  onImageChange?: (idx: number, image?: App.Image) => void
  viewPriceInPoints?: boolean
}

const imageParams: ImageParams = {
  aspectRatio: '16:9',
  desktopWidth: '67vw',
  largeDesktopWidth: '750px',
  quality: 'good',
}

const flashOfferTypes = [OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR]

function MainOfferTile(props: Props) {
  const {
    offer,
    eagerLoadFirstImage,
    offerUrl,
    onImageChange,
    viewPriceInPoints,
  } = props

  const {
    property,
    name,
    offerFlightsEnabled,
    lowestPricePackage,
    travelToDate,
  } = offer
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const tour = lowestPricePackage?.tour
  const vendorLogoAlt = property?.name
    ? intl.formatMessage(
        {
          id: '2_i5XyiMorvX34uWdBJt',
          defaultMessage: '{vendorName} logo',
          description: 'Alternative text for an offer tile vendor logo image when the vendor name is available',
        },
        { vendorName: property.name },
      )
    : intl.formatMessage({
        id: 'i14-PVWNxo4GGWz6Xqbu',
        defaultMessage: 'Vendor logo',
        description: 'Alternative text for an offer tile vendor logo image when the vendor name is unavailable',
      })
  const tourLogoAlt = tour?.name
    ? intl.formatMessage(
        {
          id: 'pVAVG0r19mEeaFV2saEv',
          defaultMessage: '{tourName} logo',
          description: 'Alternative text for an offer tile tour logo image when the tour name is available',
        },
        { tourName: tour.name },
      )
    : intl.formatMessage({
        id: '6JZFvZKvtF9GnO6OPAMX',
        defaultMessage: 'Tour logo',
        description: 'Alternative text for an offer tile tour logo image when the tour name is unavailable',
      })

  const { currentRegionCode, currentRegionHasFlights } = useContext(GeoContext)

  const showAdditionalImages = flashOfferTypes.includes(offer.type) && offer.images.length > 3
  const isFlash = flashOfferTypes.includes(offer.type)

  const cancellationPolicy = lowestPricePackage?.roomRate?.cancellationPolicy
  const isFlexibleCancellation = isFlexibleCancellationPolicy(cancellationPolicy)
  const isCruise = offer.holidayTypes?.includes('Cruises')
  const luxPlusEnabled = useAppSelector(state => getIsLuxPlusEnabled(state))
  const inclusionsViewableLuxPlusTier = useAppSelector(getInclusionsViewableLuxPlusTier)

  const cruiseLocation = useMemo(() => {
    if (isCruise && offer.startLocation && offer.endLocation) {
      return offer.startLocation === offer.endLocation
        ? intl.formatMessage(
            {
              id: 'SFj1tMynK9zl2io0DDSZ',
              defaultMessage: 'Starts and ends in {location}',
              description: 'Cruise offer tile location label when start and end ports are the same',
            },
            { location: offer.startLocation },
          )
        : intl.formatMessage(
            {
              id: 'IpwOm9tLFZJGItxemPB_',
              defaultMessage: 'Starts in {startLocation}, Ends in {endLocation}',
              description: 'Cruise offer tile location label showing distinct start and end ports',
            },
            { startLocation: offer.startLocation, endLocation: offer.endLocation },
          )
    }
    return
  }, [isCruise, offer, intl])

  const location = offer.type === OFFER_TYPE_TOUR
    ? offer.location
    : (offer.locationHeading && offer.locationSubheading
        ? intl.formatMessage(
            {
              id: 'RDK8Wg2uGXN_yB2q5Gxi',
              defaultMessage: '{locationHeading}, {locationSubheading}',
              description: 'Offer tile location heading and subheading separated by comma',
            },
            { locationHeading: offer.locationHeading, locationSubheading: offer.locationSubheading },
          )
        : (offer.locationHeading || offer.locationSubheading || ''))
  const pricingSection = <BottomSegmentGrid>
    <BottomSegmentDetails>
      {offer.bundledWithFlightsOnly && <FrontPageTileFlightOnlyPricing offer={offer} />}
      {!offer.bundledWithFlightsOnly && lowestPricePackage &&
        <OfferTilePricing
          offer={offer}
          pkg={lowestPricePackage}
          offerUrl={offerUrl}
          desktopAlign="center"
          viewPriceInPoints={viewPriceInPoints}
        />}
    </BottomSegmentDetails>
    <BottomSegmentCTA direction="vertical">
      <TextButton kind="primary">
        <CSSBreakpoint max="tablet">{intl.formatMessage({
          id: 'D0v8mAmdy1sWaJg3dsiV',
          defaultMessage: 'View',
          description: 'Short CTA label on mobile to view offer details in the offer listing section',
        })}
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">{intl.formatMessage({
          id: 'tt_wCfGPzBPA-clrDWT8',
          defaultMessage: 'View Offer',
          description: 'CTA label on desktop to view offer details on the offer page',
        })}
        </CSSBreakpoint>
      </TextButton>
    </BottomSegmentCTA>
    <BottomSegmentExtra direction="vertical" horizontalAlign="start" desktopHorizontalAlign="center" noDisplayWhenEmpty>
      {currentRegionHasFlights && offerFlightsEnabled && !offer.bundledWithFlightsOnly &&
        <FrontPageTileFlightPricing offer={offer} />}
    </BottomSegmentExtra>
  </BottomSegmentGrid>

  const isUSRegion = currentRegionCode === 'US'

  const memberPrice = lowestPricePackage?.memberPrice ?? 0
  const showMemberPrice = luxPlusEnabled && memberPrice > 0
  const saveAmount = showMemberPrice ? (lowestPricePackage?.price ?? 0) - memberPrice : undefined
  const luxPlusInclusions = lowestPricePackage?.luxPlusInclusionsByTier?.[inclusionsViewableLuxPlusTier]

  const wall = <OfferTileLoggedInButtons
    discountPercentage={lowestPricePackage?.defaultPricing?.discountPercent ?? 0}
    align="none"
    fullWidth
  />

  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (!isLEHotel(offer) || offer.bundledWithFlightsOnly || !lowestPricePackage || !isHotelPackage(lowestPricePackage)) {
      return []
    }
    return [
      generateLoyaltyPointsCalculatorHotelOptions(offer, {
        pkg: lowestPricePackage,
        price: lowestPricePackage.price + lowestPricePackage.propertyFees,
        memberPrice: lowestPricePackage.memberPrice ? (lowestPricePackage.memberPrice + lowestPricePackage.propertyFees) : 0,
        currentRegionCode,
        nights: lowestPricePackage.duration,
      }),
    ]
  }, [offer, lowestPricePackage, currentRegionCode])

  const hotelProductType = getLoyaltyProductType(offer)
  const burnProductType = (hotelProductType === 'hotel_flash' || hotelProductType === 'hotel_lpc') ? hotelProductType : undefined
  const { hasSelectedBurnToggle } = useSearchBurnEnabled()
  const isViewPriceInPoints = !!viewPriceInPoints && !hasSelectedBurnToggle
  const earnBurnPrice = (lowestPricePackage && isHotelPackage(lowestPricePackage)) ? lowestPricePackage.price + lowestPricePackage.propertyFees : 0
  const earnBurnResult = useSearchBurnPoints(earnBurnPrice, burnProductType, getHotelEncryptedMargin(offer.type, lowestPricePackage), {
    disabled: pointsEarnCalculationRequests.length === 0,
    viewPriceInPoints: isViewPriceInPoints,
  })

  return (
    <ProductPaletteProvider product={offer}>
      <Main border="light-foreground" borderRadius="S">
        <MainImageSegment className={clsx({ additional: showAdditionalImages })}>
          <ImageCarousel
            images={offer.images}
            imageParams={imageParams}
            eagerLoadFirstImage={eagerLoadFirstImage}
            topLeft={<LabelGroup>
              <ProductTypeLabel productType={offer.productType} />
              <OfferPartnershipLabel offer={offer} />
              {offer?.availabilitySchedule?.end && (
                <OfferUrgencyCountdownLabel
                  endDate={offer.availabilitySchedule.end}
                  tagPlace="offer-main"
                />
              )}
            </LabelGroup>}
            topRight={offer.badge && <OfferBadge badge={offer.badge} />}
            onImageChange={onImageChange}
            onImageLoad={handleImageLoaded}
          />
          {showAdditionalImages && <AdditionalImages>
            {take(offer.images, 3, 1).map((image) => (
              <Image
                key={image.id}
                id={image.id}
                alt={name}
                height={120}
                width={245}
                loading="lazy"
                fit="center"
              />
            ))}
          </AdditionalImages>}
        </MainImageSegment>
        <DetailContainer>
          <CSSBreakpoint min="desktop">
            <Group direction="horizontal" horizontalAlign="space-between">
              {!isUSRegion && isOfferRatingDisplayable(offer.property?.rating) && (
                <OfferRating
                  variant="medium"
                  rating={offer.property.rating}
                />
              )}
              <TripGuard>
                <BookmarkButton offer={offer} />
              </TripGuard>
            </Group>
          </CSSBreakpoint>
          <DetailSegment>
            <TripGuard>
              <CSSBreakpoint max="tablet">
                <OffsetBookmarkContainer>
                  <BookmarkButton offer={offer} />
                </OffsetBookmarkContainer>
              </CSSBreakpoint>
            </TripGuard>
            <Group direction="vertical" gap={8}>
              <OfferLabels
                offer={offer}
                desktopHorizontalAlign="center"
                saveAmount={saveAmount}
                showMemberInclusionLabels
                luxPlusInclusions={luxPlusInclusions}
              />

              <Group direction="horizontal" desktopDirection="vertical-reverse" horizontalAlign="space-between" desktopHorizontalAlign="center" verticalAlign="center" gap="4 16">
                <Group direction="vertical" horizontalAlign="start" desktopHorizontalAlign="center">
                  <BodyText variant="large">{cruiseLocation || location}</BodyText>
                  <Heading variant="heading6">{property?.name ?? offer.name}</Heading>
                </Group>
                <div>
                  {property?.logoImageId && <Logo id={property.logoImageId} width="300" aspectRatio="3:2" alt={vendorLogoAlt} />}
                  {tour?.logoImageId && <Logo id={tour.logoImageId} width="300" aspectRatio="3:2" alt={tourLogoAlt} />}
                </div>
              </Group>

              {isOfferRatingDisplayable(offer.property?.rating) && (
                <>
                  <CSSBreakpoint max="tablet">
                    <OfferRating
                      inlineLabel
                      variant="xs"
                      rating={offer.property.rating}
                    />
                  </CSSBreakpoint>
                  {isUSRegion && (
                    <CSSBreakpoint min="desktop">
                      <USDesktopRating
                        inlineLabel
                        variant="xs"
                        rating={offer.property.rating}
                      />
                    </CSSBreakpoint>
                  )}
                </>
              )}

              <OfferContent>
                {property && <BodyText variant="large">{offer.name}</BodyText>}
                {isCruise &&
                  <VerticalSpacer gap={0}>
                    { offer.vendorName &&
                      <BodyText variant="large" weight="normal">
                        {intl.formatMessage(
                          {
                            id: '66mbbnoPNyM-p759-qm8',
                            defaultMessage: 'Cruise line: {vendorName}',
                            description: 'Label showing the cruise line vendor name on offer tile',
                          },
                          { vendorName: offer.vendorName },
                        )}
                      </BodyText>}
                    { offer.vendorVehicle &&
                      <BodyText colour="primary" variant="large" weight="bold">
                        {intl.formatMessage(
                          {
                            id: '5QQFSuvCuOObOtFbh00d',
                            defaultMessage: 'Cruise ship: {vendorVehicle}',
                            description: 'Label showing the cruise ship name on the offer tile',
                          },
                          { vendorVehicle: offer.vendorVehicle },
                        )}
                      </BodyText>}

                  </VerticalSpacer>}

                {isFlash && isFlexibleCancellation && cancellationPolicy && <OfferPropertyFlexibleCancellationPolicies cancellationPolicy={cancellationPolicy} propertyTimezoneOffset={property?.timezoneOffset} />}
                {isFlash && isCruise && travelToDate && <Caption colour="primary" variant="large" weight="bold">
                  {intl.formatMessage(
                    {
                      id: 'VLXuWxn5nv_v-PcSBEU_',
                      defaultMessage: 'Sailing until {date}',
                      description: 'Cruise offer travel end date on offer tile in the cruise section',
                    },
                    { date: intlDateFormatter(travelToDate, INTL_DMY_CASUAL_FORMAT) },
                  )}
                </Caption>}
                {isFlash && !isCruise && travelToDate && <Caption colour="primary" variant="large" weight="bold">
                  {intl.formatMessage(
                    {
                      id: 'RGdEMU4NHhYVA3wOQOF5',
                      defaultMessage: 'Travel until {date}',
                      description: 'Offer travel end date on offer tile in the date selection or calendar picker',
                    },
                    { date: intlDateFormatter(travelToDate, INTL_DMY_CASUAL_FORMAT) },
                  )}
                </Caption>}
                {(!!offer.badge || offer.bundledWithFlightsOnly) && <OfferBadgeTagWrapper>
                  {offer.badge && <OfferBadgeLabel badge={offer.badge} />}
                </OfferBadgeTagWrapper>}
                <LoyaltyPointsWrapper>
                  <MobileLoyaltyPointsBreakpoint>
                    <LoyaltyPointsEarn
                      calculationRequests={pointsEarnCalculationRequests}
                      calculationType="estimate"
                      pointsToApply={earnBurnResult.pointsToBurn}
                      burnPriceInPoints={earnBurnResult.priceInPoints}
                    />
                  </MobileLoyaltyPointsBreakpoint>
                  <DesktopLoyaltyPointsBreakpoint>
                    <LoyaltyPointsEarn
                      calculationRequests={pointsEarnCalculationRequests}
                      calculationType="estimate"
                      horizontalAlign="center"
                      pointsToApply={earnBurnResult.pointsToBurn}
                      burnPriceInPoints={earnBurnResult.priceInPoints}
                    />
                  </DesktopLoyaltyPointsBreakpoint>
                </LoyaltyPointsWrapper>
              </OfferContent>
            </Group>

            <BottomSegment>
              <WalledContent enforced={offer.walledGarden} wall={wall}>
                {pricingSection}
              </WalledContent>
            </BottomSegment>
          </DetailSegment>
        </DetailContainer>
      </Main>
    </ProductPaletteProvider>
  )
}

export default MainOfferTile
